/* eslint-disable no-use-before-define -- P1-3 (2026-08-26): 命中均为回调/WS 重连逻辑引用(渲染后执行), 28阶段审计确认安全 */
/**
 * 语音会话 Provider（S2 客户端核心）
 *
 * 职责：
 *   - WSS 客户端（/ws/voice，首帧 Bearer 鉴权，指数退避重连 V-EDGE-01）
 *   - 状态机镜像（off/connecting/active/suspended）+ 三态 UI（immersive/capsule/hidden）
 *   - 净时长展示（usage 心跳）+ 语音时光三档视觉数据（V-UI-14）
 *   - Action Broker 客户端执行器：navigate / prefill / highlight / ui_command（V-ARCH-04）
 *   - 首次告知（V-COMP-01）、达量弹窗（V-UI-16）、单会话冲突接管（V-ARCH-07）
 *   - 刷新恢复（V-EDGE-02）：mount 时查 /session/active，提示一键续接
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { getToken } from '../api';
import { useAssistant } from '../hooks/useAssistant';
import { appLocale } from '../i18n';
import { VoiceAudio } from './audio';

const VoiceContext = createContext(null);
export const useVoice = () => useContext(VoiceContext);

function wsUrl() {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const base = (api.defaults.baseURL || '').replace(/\/api$/, '');
  return `${proto}//${window.location.host}${base}/ws/voice`;
}

export function VoiceProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; }, [location]);
  const { activeAssistant } = useAssistant();
  const { assistants } = useAssistant();

  // 语音动作跳转统一走助理作用域：/knowledge 等路由只挂在 /assistant/:publicId 下，
  // 裸跳顶层会被 catch-all 兜回 dashboard。'default' 由 AssistantRouteScope 解析为当前助理。
  // /assistants 与 /change-password 是顶级路由例外（/change-password 无助理作用域版本）。
  const scopedNavigate = (target) => {
    if (!target) return;
    if (target.startsWith('/assistant/') || target === '/assistants' || target === '/change-password') return navigate(target);
    return navigate('/assistant/default' + target);
  };

  const [needAssistantOpen, setNeedAssistantOpen] = useState(false); // 零助理引导（2026-08-17 定稿）
  const [pickerFor, setPickerFor] = useState(null); // 首次音色选择 { assistantId, name, gender }

  const [featureEnabled, setFeatureEnabled] = useState(false); // 服务端权益可见性
  const [voiceOn, setVoiceOnState] = useState(true);           // 用户全局开关（V-UI-09）
  const [state, setState] = useState('off');                   // off/connecting/active/suspended
  const [uiMode, setUiMode] = useState('immersive');           // immersive/capsule/hidden
  const [speaking, setSpeaking] = useState(null);              // null/user/assistant
  const [transcripts, setTranscripts] = useState([]);          // {role,text,final,citation?}
  const [remainingSec, setRemainingSec] = useState(null);
  const [grantedSec, setGrantedSec] = useState(null);
  const [billedSec, setBilledSec] = useState(0);
  const [consentOpen, setConsentOpen] = useState(false);
  const [quota, setQuota] = useState(null);                    // {state, resumeDate}
  const [quotaModalOpen, setQuotaModalOpen] = useState(false);
  const [conflict, setConflict] = useState(null);              // 单会话锁冲突
  const [resumeBlocked, setResumeBlocked] = useState(false);   // 在场证明第 3 阶
  const [resumeHint, setResumeHint] = useState(null);          // 刷新恢复提示
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  const [micError, setMicError] = useState(null);              // 麦克风被拒（V-EDGE-07）
  const [spotlight, setSpotlight] = useState(null);            // {anchor, hint}
  const [alertNotice, setAlertNotice] = useState(null);            // {text, until} 2026-08-22：看门狗取消/重置/重建可见性
  const [opConfirm, setOpConfirm] = useState(null);              // {callId, op, hint} 2026-08-23 Tier-2：确认门控代操作的待确认态
  const [motionUrl, setMotionUrl] = useState(null);            // S3 小动作剪辑（就绪才非空）
  const [portraitUrl, setPortraitUrl] = useState(null);        // portraitMaster 半身像（标准卡片降级链）
  const [motionToast, setMotionToast] = useState(null);        // 静默升级情绪提示（V-AVATAR-13）
  const [voiceError, setVoiceError] = useState(false);         // 失败态红光（V-UI-X：与成功绿光区分）

  const wsRef = useRef(null);
  const motionSeenRef = useRef({}); // assistantId → 首轮查询已完成（首轮即就绪不弹提示）
  const audioRef = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timer: null, wantLive: false });
  const pendingStartRef = useRef(null);
  const lastStartRef = useRef(null);
  const restartAfterReconnectRef = useRef(false);
  // 对话页注册的会话目标 { id, assistantId }（V-XMOD-02），语音会话落库到该会话
  const conversationTargetRef = useRef(null);
  // 语音导航栈（V-UI-11）：只记录语音发起跳转前的所在页面，"回去"时弹栈回跳；
  // 用户手动点击不入栈——"回去"的语义 = 回到助理带你离开的那个页面。上限 10 层。
  const navStackRef = useRef([]);
  const recordNavOrigin = useCallback(() => {
    const p = locationRef.current.pathname;
    const s = navStackRef.current;
    if (s[s.length - 1] !== p) s.push(p);
    if (s.length > 10) s.shift();
  }, []);
  const popNavOrigin = useCallback(() => navStackRef.current.pop() || null, []);
  const sendActionAck = useCallback((callId, ok, reason, action) => {
    const ws = wsRef.current;
    if (!callId || !ws || ws.readyState !== WebSocket.OPEN) return;
    try { ws.send(JSON.stringify({ type: 'action_ack', callId, ok, reason: reason || null, action: action || null })); } catch (e) { /* */ }
  }, []);
  const setVoiceConversationTarget = useCallback((target) => {
    conversationTargetRef.current = target && target.id ? target : null;
  }, []);
  const readyRef = useRef(false);
  const locale = appLocale().startsWith('zh') ? 'zh' : 'en';

  // ── 权益/开关加载 ──
  const refreshEntitlement = useCallback(async () => {
    try {
      const [ent, pref] = await Promise.all([
        api.get('/voice/entitlement'),
        api.get('/voice/preference'),
      ]);
      setFeatureEnabled(!!ent.data.enabled);
      setVoiceOnState(pref.data.enabled !== false);
      if (ent.data.totalRemainingSec !== null && ent.data.totalRemainingSec !== undefined) {
        setRemainingSec(ent.data.totalRemainingSec);
      }
      const granted = (ent.data.monthly?.grantedSec || 0)
        + (ent.data.packs || []).reduce((s, p) => s + p.totalSec, 0)
        + (ent.data.trial?.grantedSec || 0);
      if (granted > 0) setGrantedSec(granted);
      return ent.data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => { refreshEntitlement(); }, [refreshEntitlement]);

  // ── S3 小动作剪辑：切助理即查一次；会话中每 60s 轮询，后台生成完静默升级（V-AVATAR-13） ──
  const fetchMotion = useCallback(async (assistantId) => {
    if (!assistantId) return;
    try {
      const r = await api.get(`/voice/avatar-video/${assistantId}`);
      const url = r.data && r.data.url ? r.data.url : null; // 文件名含 source_hash，重生成自然换 URL，无需手动 bust
      const pUrl = r.data && r.data.portraitUrl ? r.data.portraitUrl : null;
      setPortraitUrl((prev) => (prev === pUrl ? prev : pUrl));
      setMotionUrl((prev) => {
        // 首轮即就绪 → 直接上视频不打扰；会话中途就绪 → 情绪提示
        if (!prev && url && motionSeenRef.current[assistantId]) setMotionToast(assistantId);
        return url;
      });
      motionSeenRef.current[assistantId] = true;
    } catch (e) { /* 保持静态图，零感知 */ }
  }, []);

  useEffect(() => {
    setMotionUrl(null);
    setPortraitUrl(null);
    setMotionToast(null);
    if (activeAssistant?.id) fetchMotion(activeAssistant.id);
  }, [activeAssistant?.id, fetchMotion]);

  useEffect(() => {
    if (state === 'off' || !activeAssistant?.id) return undefined;
    const t = setInterval(() => fetchMotion(activeAssistant.id), 60000);
    return () => clearInterval(t);
  }, [state, activeAssistant?.id, fetchMotion]);

  // 刷新恢复提示（V-EDGE-02）
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/voice/session/active');
        if (r.data.active) setResumeHint(r.data);
      } catch (e) { /* 忽略 */ }
    })();
  }, []);

  const setVoiceOn = useCallback(async (on) => {
    setVoiceOnState(on);
    try { await api.post('/voice/preference', { enabled: on }); } catch (e) { /* 忽略 */ }
    if (!on) teardown(false); // 通话中关闭 → 正常收尾
  }, []); // eslint-disable-line

  // ── 音频 ──
  const ensureAudio = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new VoiceAudio({
        onChunk: (b64) => {
          const ws = wsRef.current;
          if (mutedRef.current) return; // 静音/通话保持: 本地闸门, 不上行
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'audio', audio: b64 }));
          }
        },
      });
    }
    return audioRef.current;
  }, []);

  const startMic = useCallback(async () => {
    try {
      await ensureAudio().startCapture();
      setMicError(null);
      return true;
    } catch (e) {
      const name = e && e.name;
      setMicError(
        name === 'NotSecureContextError' ? 'insecure'
          : name === 'NotAllowedError' ? 'denied'
          : name === 'NotFoundError' || name === 'OverconstrainedError' ? 'notfound'
          : name === 'NotReadableError' ? 'busy'
          : 'error'
      );
      return false;
    }
  }, [ensureAudio]);

  // ── 会话控制 ──
  const teardown = useCallback((sendEnd = true) => {
    reconnectRef.current.wantLive = false;
    clearTimeout(reconnectRef.current.timer);
    readyRef.current = false;
    const ws = wsRef.current;
    lastStartRef.current = null;
    restartAfterReconnectRef.current = false;
    if (ws) {
      if (sendEnd && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'end' })); } catch (e) { /* */ }
        // 告别兜底：给服务端告别流程留时间，2s 后本地强制清理
        setTimeout(() => { try { ws.close(); } catch (e) { /* */ } }, 2000);
      } else {
        try { ws.close(); } catch (e) { /* */ }
      }
      wsRef.current = null;
    }
    if (audioRef.current) audioRef.current.destroy();
    audioRef.current = null;
    setState('off');
    setSpeaking(null);
    setMuted(false);
    setResumeBlocked(false);
    navStackRef.current = []; // 会话结束清空导航栈（V-UI-11：跨会话不遗留旧来源）
  }, []);

  const handleServerMessage = useCallback((msg) => {
    switch (msg.type) {
      case 'ready':
        readyRef.current = true;
        if (pendingStartRef.current) {
          const p = pendingStartRef.current;
          pendingStartRef.current = null;
          wsRef.current && wsRef.current.send(JSON.stringify(p));
        } else if (restartAfterReconnectRef.current && lastStartRef.current) {
          // 上游/连接异常重连后自动重发 start（reattach 让服务端按保温期续接）
          restartAfterReconnectRef.current = false;
          wsRef.current && wsRef.current.send(JSON.stringify({ ...lastStartRef.current, reattach: true }));
        }
        break;
      case 'consent_required':
        setConsentOpen(true);
        break;
      case 'session':
        if (msg.conflict) { setConflict(msg); break; }
        setConflict(null);
        setState('connecting');
        break;
      case 'state':
        if (msg.state) setState(msg.state);
        if (msg.speaking !== undefined) setSpeaking(msg.speaking);
        if (msg.resumeBlocked) setResumeBlocked(true);
        break;
      case 'transcript':
        setTranscripts((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (msg.final) {
            // 流式半句后到达完整句：直接落定最后一条同角色未完成项，避免“半句+完整句”重复
            if (last && last.role === msg.role && !last.final) {
              next[next.length - 1] = { ...last, text: msg.text, final: true };
              return next.slice(-100);
            }
            // 重连/上游重发的完全一致最终稿去重
            if (last && last.role === msg.role && last.final && last.text === msg.text) return next;
            next.push({ role: msg.role, text: msg.text, final: true });
          } else {
            if (last && last.role === msg.role && !last.final) {
              next[next.length - 1] = { ...last, text: msg.text };
            } else {
              next.push({ role: msg.role, text: msg.text, final: false });
            }
          }
          return next.slice(-100);
        });
        break;
      case 'audio':
        audioRef.current && audioRef.current.playChunk(msg.audio);
        setSpeaking('assistant');
        break;
      case 'citation':
        setTranscripts((prev) => [...prev, { role: 'citation', citation: msg.citation, final: true }].slice(-100));
        break;
      case 'action':
        executeAction(msg, { navigate: scopedNavigate, setUiMode, setMuted, setState, recordOrigin: recordNavOrigin, popOrigin: popNavOrigin, sendAck: sendActionAck, setOpConfirm });
        break;
      case 'usage':
        if (msg.remainingSec !== null && msg.remainingSec !== undefined) setRemainingSec(msg.remainingSec);
        if (msg.billedSec !== undefined) setBilledSec(msg.billedSec);
        break;
      case 'quota':
        if (msg.state === 'exhausted') {
          setQuota({ state: 'exhausted', resumeDate: msg.resumeDate });
          // 让助理说完当前这句再弹（V-UI-16）：等服务端 ended 事件再开弹窗
        } else if (msg.state === 'warn95') {
          setQuota({ state: 'warn95', remainingSec: msg.remainingSec });
        }
        break;
      case 'ended':
        if (msg.reason === 'quota_exhausted') setQuotaModalOpen(true);
        teardown(false);
        break;
      case 'alert': {
        // 2026-08-22 可见性（最佳实践：取消/重置/重建不再静默）：本地化短讯 + 自动消退
        const ALERT_TEXTS = {
          response_cancelled: { zh: '刚才这轮卡住了，已取消，可以再说一次', en: 'That reply stalled — canceled. Try again?' },
          context_reset: { zh: '上下文已重置以恢复响应', en: 'Context reset to restore responsiveness' },
          context_rebuild: { zh: '已重新连接，上下文以摘要形式恢复', en: 'Reconnected — context restored in summary form' },
          interruption: { zh: '打断了，先处理你这句', en: 'Heard you — switching now' },
          presence_ladder: { zh: '长时间未活动，会话已休眠——点击继续', en: 'Session hibernated after inactivity — tap to continue' },
        };
        // 2026-08-23（v4）：取消/打断告警 = 助理已停止 → 立即排空已缓冲音频（防被中断话语的续播）
        // 2026-08-23（全谱整改）：无条件排空（原 isPlaying 门控在句间静默期漏停——被取消话语
        // 的后续分片可再启动播放）；空闲时排空为无害空操作（下一分片重建上下文）。
        if (msg.kind === 'interruption' || msg.kind === 'response_cancelled') {
          if (audioRef.current && typeof audioRef.current.stopPlayback === 'function') {
            audioRef.current.stopPlayback();
          }
        }
        const alertEntry = ALERT_TEXTS[msg.kind];
        if (alertEntry) {
          const alertText = (appLocale().startsWith('zh') ? alertEntry.zh : alertEntry.en) || msg.message || '';
          if (alertText) setAlertNotice({ text: alertText, until: Date.now() + 6000 });
        }
        break;
      }
      case 'error':
        if (msg.code === 'NO_ENTITLEMENT') setFeatureEnabled(false);
        if (msg.code === 'UPSTREAM_ERROR' || msg.code === 'UPSTREAM_CLOSED') {
          restartAfterReconnectRef.current = true;
          scheduleReconnect();
          setVoiceError(true);
        }
        break;
      default:
        break;
    }
  }, [navigate, teardown]); // eslint-disable-line

  // 失败红光自动消退（V-UI-X：不常驻，避免与成功绿光混淆）
  useEffect(() => {
    if (!voiceError) return undefined;
    const timer = setTimeout(() => setVoiceError(false), 3500);
    return () => clearTimeout(timer);
  }, [voiceError]);

  // 告警短讯自动消退（2026-08-22：看门狗取消/上下文重置/重建交接的可见性）
  useEffect(() => {
    if (!alertNotice) return undefined;
    const timer = setTimeout(() => setAlertNotice(null), Math.max(0, alertNotice.until - Date.now()));
    return () => clearTimeout(timer);
  }, [alertNotice]);

  const scheduleReconnect = useCallback(() => {
    const rc = reconnectRef.current;
    if (!rc.wantLive) return;
    clearTimeout(rc.timer);
    const delay = Math.min(30000, 1000 * Math.pow(2, rc.attempts++)); // 指数退避
    rc.timer = setTimeout(() => { if (rc.wantLive) connect(); }, delay);
  }, []); // eslint-disable-line

  const connect = useCallback(() => {
    const token = getToken();
    if (!token) return;
    const prev = wsRef.current;
    if (prev) {
      // 替换旧连接前先摘掉 close 回调，避免触发额外的指数退避重连
      prev.onclose = null;
      wsRef.current = null;
      try { prev.close(); } catch (e) { /* 忽略 */ }
    }
    readyRef.current = false;
    const ws = new WebSocket(wsUrl());
    wsRef.current = ws;
    ws.onopen = () => {
      reconnectRef.current.attempts = 0;
      ws.send(JSON.stringify({ type: 'auth', token }));
    };
    ws.onmessage = (ev) => {
      let msg = null;
      try { msg = JSON.parse(ev.data); } catch (e) { return; }
      handleServerMessage(msg);
    };
    ws.onclose = () => {
      if (reconnectRef.current.wantLive) scheduleReconnect();
    };
    ws.onerror = () => { /* onclose 会跟上 */ };
  }, [handleServerMessage, scheduleReconnect]);

  /** 开始语音（唯一入口=用户主动点击，V-UI-08）。 */
  // V-EDGE-04: Media Session — 切标签页/锁屏时像接电话一样保持会话;
  // 锁屏暂停键=麦克风保持(不上行), 播放键=恢复送话。会话本身不断。
  useEffect(() => {
    if (!('mediaSession' in navigator)) return undefined;
    if (state === 'off') {
      try {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = 'none';
      } catch (_) { /* 老浏览器 */ }
      return undefined;
    }
    const name = (activeAssistant && (activeAssistant.name || activeAssistant.persona_name)) || 'OzStaff';
    const avatarPath = activeAssistant && (activeAssistant.avatar_path || (activeAssistant.persona && activeAssistant.persona.avatar_path));
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: name,
        artist: 'OzStaff',
        artwork: avatarPath ? [{ src: `/uploads/${String(avatarPath).split('?')[0].split('/').pop()}`, sizes: '256x256' }] : [],
      });
      navigator.mediaSession.playbackState = state === 'suspended' ? 'paused' : 'playing';
      navigator.mediaSession.setActionHandler('pause', () => setMuted(true));
      navigator.mediaSession.setActionHandler('play', () => setMuted(false));
    } catch (_) { /* 老浏览器无 MediaMetadata */ }
    return () => {
      try {
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('play', null);
      } catch (_) { /* ignore */ }
    };
  }, [state, activeAssistant]);

  const startVoice = useCallback(async (opts = {}) => {
    // 零助理：禁止开播，引导先创建助理（2026-08-17 定稿）
    if (!assistants || assistants.length === 0) { setNeedAssistantOpen(true); return; }
    if (!activeAssistant) return;

    // 首次使用引导配置音色：人设未配 voice_id 且未跳过 → 先弹选择器（开播前，不动麦克风）
    if (!opts.skipPicker) {
      try {
        const dismissed = localStorage.getItem(`voice_picker_dismissed_${activeAssistant.id}`) === '1';
        if (!dismissed) {
          const pv = await api.get(`/voice/persona-voice?assistantId=${activeAssistant.id}`);
          if (pv.data && !pv.data.voiceId) {
            const pvLang = pv.data && (pv.data.lang === 'ja' || pv.data.lang === 'zh' || pv.data.lang === 'en')
              ? pv.data.lang : (locale === 'en' ? 'en' : 'zh');
            setPickerFor({ assistantId: activeAssistant.id, name: activeAssistant.name || '', gender: pv.data.gender || null, lang: pvLang });
            return;
          }
        }
      } catch (_) { /* 查询失败不阻断开播 */ }
    }

    setTranscripts([]);
    setBilledSec(0);
    setQuota(null);
    setUiMode('immersive');
    setState('connecting');
    reconnectRef.current.wantLive = true;

    // 先取麦克风权限（被拒则不开会话，V-EDGE-07）
    const micOk = await startMic();
    if (!micOk) { setState('off'); reconnectRef.current.wantLive = false; return; }

    // 跨模态会话归属（V-XMOD-02）：对话页注册的会话目标，仅当助理一致时携带
    const target = conversationTargetRef.current;
    pendingStartRef.current = {
      type: 'start',
      assistantId: activeAssistant.id,
      lang: locale,
      reattach: !!opts.reattach,
      voiceId: opts.voiceId || undefined,
      conversationId: target && target.assistantId === activeAssistant.id ? target.id : undefined,
    };
    lastStartRef.current = { ...pendingStartRef.current };
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // 复用连接（重连后）：已 ready 直接发 start，否则等 ready 回执统一发
      if (readyRef.current) {
        wsRef.current.send(JSON.stringify(pendingStartRef.current));
        pendingStartRef.current = null;
      }
    }
    connect();
  }, [activeAssistant, assistants, locale, connect, startMic]);

  // 首次音色选择器动作（选定=长期写入人设；跳过=本次默认且不再提醒该助理）
  const pickerClose = useCallback(() => {
    setPickerFor(null);
  }, []);

  const pickerSkip = useCallback(() => {
    if (pickerFor) { try { localStorage.setItem(`voice_picker_dismissed_${pickerFor.assistantId}`, '1'); } catch (_) {} }
    setPickerFor(null);
    startVoice({ skipPicker: true });
  }, [pickerFor, startVoice]);

  const pickerChoose = useCallback(async (voiceId) => {
    if (pickerFor && voiceId) {
      try { await api.put('/settings/persona', { voice_id: voiceId }); } catch (_) { /* 保存失败仍用默认开播 */ }
    }
    setPickerFor(null);
    startVoice({ skipPicker: true });
  }, [pickerFor, startVoice]);

  const endVoice = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'end' })); } catch (e) { /* */ }
      setTimeout(() => teardown(false), 2500); // 给告别流程留时间
    } else {
      teardown(false);
    }
  }, [teardown]);

  const takeover = useCallback(() => {
    setConflict(null);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && activeAssistant) {
      ws.send(JSON.stringify({ type: 'takeover', assistantId: activeAssistant.id, lang: locale }));
    }
  }, [activeAssistant, locale]);

  const resumeConfirm = useCallback(() => {
    setResumeBlocked(false);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'resume_confirm' }));
    }
    startMic(); // 重新启用麦克风
  }, [startMic]);

  /** 挂起中点击胶囊恢复（V-SAFE-12 前两阶开口即恢复 = 重新启用麦克风说话） */
  const resumeFromSuspend = useCallback(() => {
    if (resumeBlocked) return; // 第 3 阶必须点确认键
    startMic();
  }, [resumeBlocked, startMic]);

  const keyboardSend = useCallback((text) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && text.trim()) {
      ws.send(JSON.stringify({ type: 'text', text: text.trim() }));
    }
  }, []);

  const acceptConsent = useCallback(async () => {
    try { await api.post('/voice/consent'); } catch (e) { /* WS 路径也会补写 */ }
    setConsentOpen(false);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'consent', accepted: true }));
    }
  }, []);

  // 挂起时释放麦克风（V-UI-10 不点击不监听）
  useEffect(() => {
    if (state === 'suspended' && audioRef.current) {
      audioRef.current.stopCapture();
    }
  }, [state]);

  // 移动端切后台/标签页隐藏 → 立即挂起并停表（V-EDGE-06）
  // 2026-08-23（切屏往返响应丢失）：页面返回=显式唤醒意图——可见性返回自动恢复
  //（仅对可见性触发的挂起；resumeBlocked 第 3 阶仍必须点击确认，语义不变）
  const visibilitySuspendRef = useRef(false);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (state !== 'off' && state !== 'suspended') {
          if (audioRef.current) audioRef.current.stopCapture();
          const ws = wsRef.current;
          if (ws && ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: 'suspend_now' })); } catch (e) { /* */ }
          }
          visibilitySuspendRef.current = true;
          setState('suspended');
        }
      } else if (document.visibilityState === 'visible' && state === 'suspended' && visibilitySuspendRef.current) {
        visibilitySuspendRef.current = false;
        resumeFromSuspend(); // 内置门控：第 3 阶（resumeBlocked）不自动恢复，仍须点击
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onVisibility);
    };
  }, [state, resumeFromSuspend]);

  // 快捷键 V 开启语音（V-UI-08）
  useEffect(() => {
    const onKey = (e) => {
      if (e.key.toLowerCase() !== 'v' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || e.target?.isContentEditable) return;
      if (featureEnabled && voiceOn && state === 'off') startVoice();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [featureEnabled, voiceOn, state, startVoice]);

  const value = {
    featureEnabled, voiceOn, setVoiceOn,
    setVoiceConversationTarget,
    needAssistantOpen, setNeedAssistantOpen, pickerFor, setPickerFor, pickerSkip, pickerChoose, pickerClose,
    state, uiMode, setUiMode, speaking,
    transcripts, remainingSec, grantedSec, billedSec, alertNotice,
    consentOpen, acceptConsent, setConsentOpen,
    quota, quotaModalOpen, setQuotaModalOpen,
    conflict, takeover, setConflict,
    resumeBlocked, resumeConfirm, resumeFromSuspend,
    resumeHint, setResumeHint,
    muted, micError, setMicError,
    spotlight, setSpotlight, opConfirm, setOpConfirm, sendAck: sendActionAck,
    motionUrl, portraitUrl, motionToast, setMotionToast, voiceError,
    startVoice, endVoice, keyboardSend,
    refreshEntitlement,
  };

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

/** Action Broker 客户端执行器（V-ARCH-04：结构化动作，非 DOM 模拟点击）。 */
function executeAction(msg, { navigate, setUiMode, setMuted, recordOrigin, popOrigin, sendAck, setOpConfirm }) {
  const { name, args } = msg;
  switch (name) {
    case 'ui_command':
      if (args.command === 'minimize') setUiMode('capsule');
      else if (args.command === 'expand') setUiMode('immersive');
      else if (args.command === 'hide') setUiMode('hidden');
      else if (args.command === 'mute') setMuted(true);
      else if (args.command === 'unmute') setMuted(false);
      // 'end' 已由服务端走告别流程，客户端无需动作
      break;
    case 'navigate':
      // 跳转时先最小化为胶囊，避免沉浸面板盖住目标页面（V-UI-02 切页面不中断）
      if (args.target) {
        setUiMode('capsule');
        recordOrigin && recordOrigin();
        navigate(args.target);
      }
      break;
    case 'go_back': {
      // 弹栈回跳（V-UI-11）：空栈回执 empty_stack，助理按 V-EMO-05 自然说明，不报错
      // 2026-08-23（P2 同类）：回跳同为页面跳转——先最小化，被跳回页面不得被沉浸面板覆盖（与 navigate/guide_to 同构）
      const target = popOrigin && popOrigin();
      if (target) { setUiMode('capsule'); navigate(target); sendAck && sendAck(msg.callId, true); }
      else sendAck && sendAck(msg.callId, false, 'empty_stack');
      break;
    }
    case 'guide_to':
      // 原子化引导：跳转 + 聚光。SpotlightOverlay 轮询等待目标挂载后落光。
      // 沉浸面板会盖住聚光灯（2026-08-18 修复）：先最小化为胶囊再跳转，让用户直接看到页面。
      if (args.target) {
        setUiMode('capsule');
        recordOrigin && recordOrigin();
        navigate(args.target);
      }
      if (args.anchor) window.dispatchEvent(new CustomEvent('voice:spotlight', { detail: { anchor: args.anchor } }));
      break;
    case 'highlight':
      setUiMode('capsule');
      window.dispatchEvent(new CustomEvent('voice:spotlight', { detail: args }));
      break;
    case 'select': {
      // Tier-1（2026-08-23）：当前页选择器切换（组件级结构化应用）。
      // 回执真伪（2026-08-23 全谱扫描）：结果回传闭环——目标页确认应用与否（voice:select-result），
      // 有界等待（700ms）；未命中/未挂载 → ack(false) 让模型知悉未落地并改报口选项，不伪报成功。
      // 2026-08-23（P2）：页面本地操作前最小化——沉浸面板不得覆盖被操作的页面（与 navigate/guide_to 同构）
      setUiMode('capsule');
      window.dispatchEvent(new CustomEvent('voice:select', { detail: { ...args, callId: msg.callId } }));
      let selectAcked = false;
      const onSelectResult = (e) => {
        if (selectAcked || !e.detail || e.detail.callId !== msg.callId) return;
        selectAcked = true;
        window.removeEventListener('voice:select-result', onSelectResult);
        sendAck && sendAck(msg.callId, e.detail.ok !== false, e.detail.ok !== false ? null : 'not_landed', 'select');
      };
      window.addEventListener('voice:select-result', onSelectResult);
      setTimeout(() => {
        if (!selectAcked) {
          selectAcked = true;
          window.removeEventListener('voice:select-result', onSelectResult);
          sendAck && sendAck(msg.callId, false, 'not_landed', 'select');
        }
      }, 700);
      break;
    }
    case 'operate':
      // Tier-2（2026-08-23）：确认门控——二次通道（确认/取消控件，非语音歧义）；用户确认方执行
      setOpConfirm({ callId: msg.callId, op: args.op, hint: args.hint || '' });
      break;
    case 'prefill': {
      // 代填真值化（2026-08-23 全谱整改）+ 落地重试（2026-08-25 内容联动配套）：
      // 跨页 guide_to 后字段尚未挂载、弹窗经确认门控后才打开——单次派发必然丢失。
      // 改为有界重试闭环：每 450ms 重发直至落地（ok 回执）或 16 次（≈7.7s）耗尽；
      // 落地即停（幂等 setState，不覆盖用户后续输入）；耗尽 ack(false) 让模型改报口选项。
      setUiMode('capsule');
      const prefillArgs = args;
      const prefillCallId = msg.callId;
      const PREFILL_MAX_ATTEMPTS = 16;
      let prefillDone = false;
      let prefillAttempts = 0;
      const tryPrefill = () => {
        if (prefillDone) return;
        prefillAttempts += 1;
        let acked = false;
        const onPrefillResult = (e) => {
          if (acked || prefillDone || !e.detail || e.detail.callId !== prefillCallId) return;
          acked = true;
          window.removeEventListener('voice:prefill-result', onPrefillResult);
          if (e.detail.ok !== false) {
            prefillDone = true;
            sendAck && sendAck(prefillCallId, true, null, 'prefill');
          } else if (prefillAttempts >= PREFILL_MAX_ATTEMPTS) {
            prefillDone = true;
            sendAck && sendAck(prefillCallId, false, e.detail.reason || 'not_landed', 'prefill');
          } else {
            setTimeout(tryPrefill, 450);
          }
        };
        window.addEventListener('voice:prefill-result', onPrefillResult);
        window.dispatchEvent(new CustomEvent('voice:prefill', { detail: { ...prefillArgs, callId: prefillCallId } }));
        setTimeout(() => {
          if (acked || prefillDone) return;
          acked = true;
          window.removeEventListener('voice:prefill-result', onPrefillResult);
          if (prefillAttempts >= PREFILL_MAX_ATTEMPTS) {
            prefillDone = true;
            sendAck && sendAck(prefillCallId, false, 'not_landed', 'prefill');
          } else {
            tryPrefill(); // 页面未挂载（无监听者）→ 直接重试
          }
        }, 450);
      };
      tryPrefill();
      break;
    }
    default:
      break;
  }
}
